import { evaluateBank } from './bank.ts';
import { CubicBSpline, type BSplineEvaluation, type BSplineSpan } from './bspline.ts';
import type {
  AdaptiveTableOptions,
  ArcLengthTable,
  SemanticSection,
  TrackCompilerCounters,
  TrackDefinition,
  TrackSample,
  TrackSampler,
  TrackValidationIssue,
  Vec3,
} from './types.ts';
import { TrackBuildError } from './types.ts';
import {
  EPSILON,
  arrayVec,
  clamp,
  cross,
  distance,
  dot,
  finite,
  length,
  lerp,
  normalize,
  orthogonalUnit,
  projectPerpendicular,
  rotateAroundAxis,
  scale,
  signedAngle,
  sub,
  writeVec,
} from './vector.ts';

interface RawSample extends BSplineEvaluation {
  readonly u: number;
}
interface SpanCacheEntry {
  readonly signature: string;
  readonly samples: readonly RawSample[];
}

const DEFAULT_OPTIONS: Required<AdaptiveTableOptions> = {
  maxSpacing: 0.25,
  maxChordError: 0.001,
  maxTangentAngle: Math.PI / 180,
  maxDepth: 24,
};

function mergeOptions(options?: AdaptiveTableOptions): Required<AdaptiveTableOptions> {
  const merged = { ...DEFAULT_OPTIONS, ...options };
  if (
    !(merged.maxSpacing > 0) ||
    !(merged.maxChordError > 0) ||
    !(merged.maxTangentAngle > 0) ||
    merged.maxDepth < 1
  ) {
    throw new TrackBuildError(
      'invalid_table_options',
      'Adaptive table tolerances must be positive.',
    );
  }
  return merged;
}

function evaluationAt(curve: CubicBSpline, u: number): RawSample {
  return { u, ...curve.evaluate(u) };
}

function tangentOf(sample: RawSample): Vec3 {
  const tangent = normalize(sample.derivative);
  if (length(tangent) < 0.5)
    throw new TrackBuildError(
      'degenerate_derivative',
      `Curve derivative is degenerate near u=${sample.u}.`,
      { u: sample.u },
    );
  return tangent;
}

function adaptiveSpan(
  curve: CubicBSpline,
  span: BSplineSpan,
  options: Required<AdaptiveTableOptions>,
): RawSample[] {
  const start = evaluationAt(curve, span.uStart);
  const end = evaluationAt(curve, span.uEnd);
  const output: RawSample[] = [start];
  const refine = (first: RawSample, second: RawSample, depth: number): void => {
    const middle = evaluationAt(curve, (first.u + second.u) * 0.5);
    const chord = distance(first.position, second.position);
    const splitLength =
      distance(first.position, middle.position) + distance(middle.position, second.position);
    const firstTangent = tangentOf(first);
    const secondTangent = tangentOf(second);
    const tangentAngle = Math.acos(clamp(dot(firstTangent, secondTangent), -1, 1));
    const shouldSplit =
      chord > options.maxSpacing ||
      splitLength - chord > options.maxChordError ||
      tangentAngle > options.maxTangentAngle;
    if (shouldSplit) {
      if (depth >= options.maxDepth)
        throw new TrackBuildError(
          'adaptive_refinement_limit',
          `Adaptive table exceeded depth ${options.maxDepth}.`,
          { u: middle.u },
        );
      refine(first, middle, depth + 1);
      refine(middle, second, depth + 1);
    } else {
      output.push(second);
    }
  };
  refine(start, end, 0);
  return output;
}

function sectionAt(
  sections: readonly SemanticSection[],
  distanceValue: number,
  trackLength: number,
  closed: boolean,
): number {
  for (let index = 0; index < sections.length; index += 1) {
    const section = sections[index];
    if (section.startDistance <= section.endDistance) {
      if (distanceValue >= section.startDistance && distanceValue < section.endDistance)
        return index;
    } else if (
      closed &&
      (distanceValue >= section.startDistance || distanceValue < section.endDistance)
    )
      return index;
  }
  if (!closed && distanceValue === trackLength) {
    return sections.findIndex((section) => section.endDistance === trackLength);
  }
  return -1;
}

function validateSections(sections: readonly SemanticSection[], lengthValue: number): void {
  sections.forEach((section) => {
    if (
      !section.id ||
      !Number.isFinite(section.startDistance) ||
      !Number.isFinite(section.endDistance)
    ) {
      throw new TrackBuildError(
        'invalid_semantic_section',
        'Semantic sections require an id and finite boundaries.',
      );
    }
    if (
      section.startDistance < 0 ||
      section.startDistance > lengthValue ||
      section.endDistance < 0 ||
      section.endDistance > lengthValue
    ) {
      throw new TrackBuildError(
        'semantic_section_out_of_range',
        `Section ${section.id} lies outside the track.`,
      );
    }
  });
  const intervals = sections.flatMap((section, index) =>
    section.startDistance <= section.endDistance
      ? [{ start: section.startDistance, end: section.endDistance, index }]
      : [
          { start: section.startDistance, end: lengthValue, index },
          { start: 0, end: section.endDistance, index },
        ],
  );
  for (let first = 0; first < intervals.length; first += 1) {
    for (let second = first + 1; second < intervals.length; second += 1) {
      if (intervals[first].index === intervals[second].index) continue;
      if (
        Math.max(intervals[first].start, intervals[second].start) <
        Math.min(intervals[first].end, intervals[second].end) - EPSILON
      ) {
        throw new TrackBuildError(
          'overlapping_semantic_sections',
          'Semantic section intervals may not overlap.',
        );
      }
    }
  }
}

function doubleReflectionFrames(
  samples: readonly RawSample[],
  distances: Float64Array,
  closed: boolean,
): { up: Vec3[]; right: Vec3[] } {
  // Wang et al. (2008), "Computation of Rotation Minimizing Frames". The two
  // reflections transport the frame without the torsion spikes of Frenet frames.
  const tangents = samples.map(tangentOf);
  const up: Vec3[] = [orthogonalUnit(tangents[0])];
  for (let index = 0; index < samples.length - 1; index += 1) {
    const delta = sub(samples[index + 1].position, samples[index].position);
    const deltaLengthSq = dot(delta, delta);
    let nextUp: Vec3;
    if (deltaLengthSq <= EPSILON) {
      nextUp = up[index];
    } else {
      const reflectedUp = sub(up[index], scale(delta, (2 * dot(delta, up[index])) / deltaLengthSq));
      const reflectedTangent = sub(
        tangents[index],
        scale(delta, (2 * dot(delta, tangents[index])) / deltaLengthSq),
      );
      const secondReflection = sub(tangents[index + 1], reflectedTangent);
      const secondLengthSq = dot(secondReflection, secondReflection);
      nextUp =
        secondLengthSq <= EPSILON
          ? reflectedUp
          : sub(
              reflectedUp,
              scale(secondReflection, (2 * dot(secondReflection, reflectedUp)) / secondLengthSq),
            );
    }
    nextUp = normalize(projectPerpendicular(nextUp, tangents[index + 1]));
    if (length(nextUp) < 0.5) nextUp = orthogonalUnit(tangents[index + 1]);
    up.push(nextUp);
  }
  const totalLength = distances[distances.length - 1];
  if (closed && totalLength > EPSILON) {
    const correction = signedAngle(up[up.length - 1], up[0], tangents[0]);
    for (let index = 1; index < up.length; index += 1) {
      const fraction = distances[index] / totalLength;
      up[index] = normalize(
        projectPerpendicular(
          rotateAroundAxis(up[index], tangents[index], correction * fraction),
          tangents[index],
        ),
      );
    }
  }
  return { up, right: up.map((value, index) => normalize(cross(value, tangents[index]))) };
}

function allocateTable(samples: readonly RawSample[], definition: TrackDefinition): ArcLengthTable {
  if (samples.length < 2)
    throw new TrackBuildError(
      'empty_sample_table',
      'The adaptive sample table needs at least two entries.',
    );
  const count = samples.length;
  const distances = new Float64Array(count);
  for (let index = 1; index < count; index += 1) {
    const segmentLength = distance(samples[index - 1].position, samples[index].position);
    if (!Number.isFinite(segmentLength))
      throw new TrackBuildError(
        'non_finite_arc_length',
        'Arc-length integration became non-finite.',
      );
    distances[index] = distances[index - 1] + segmentLength;
  }
  const trackLength = distances[count - 1];
  if (!(trackLength > 1e-4))
    throw new TrackBuildError('zero_length_track', 'Track length is effectively zero.');
  const sections = [...(definition.sections ?? [])];
  validateSections(sections, trackLength);
  definition.bank?.forEach((key, index) => {
    if (
      !Number.isFinite(key.distance) ||
      !Number.isFinite(key.value) ||
      (key.slope !== undefined && !Number.isFinite(key.slope))
    ) {
      throw new TrackBuildError(
        'non_finite_bank_key',
        `Bank key ${index} contains a non-finite value.`,
      );
    }
    if (key.distance < 0 || key.distance > trackLength) {
      throw new TrackBuildError(
        'bank_key_out_of_range',
        `Bank key ${index} lies outside the track.`,
      );
    }
  });
  const rmf = doubleReflectionFrames(samples, distances, definition.closed ?? false);
  const table: ArcLengthTable = {
    count,
    length: trackLength,
    closed: definition.closed ?? false,
    distance: distances,
    u: new Float64Array(count),
    position: new Float64Array(count * 3),
    derivative: new Float64Array(count * 3),
    secondDerivative: new Float64Array(count * 3),
    tangent: new Float64Array(count * 3),
    curvature: new Float64Array(count * 3),
    rmfUp: new Float64Array(count * 3),
    rmfRight: new Float64Array(count * 3),
    bank: new Float64Array(count),
    up: new Float64Array(count * 3),
    right: new Float64Array(count * 3),
    sectionIndex: new Int32Array(count),
    sections,
  };
  for (let index = 0; index < count; index += 1) {
    const sample = samples[index];
    const tangent = tangentOf(sample);
    const speedSquared = dot(sample.derivative, sample.derivative);
    const curvature = scale(
      projectPerpendicular(sample.secondDerivative, tangent),
      1 / speedSquared,
    );
    if (![sample.position, sample.derivative, sample.secondDerivative, curvature].every(finite)) {
      throw new TrackBuildError('non_finite_sample', `Sample ${index} contains non-finite data.`, {
        sampleIndex: index,
      });
    }
    const bank = evaluateBank(definition.bank, distances[index]);
    if (!Number.isFinite(bank))
      throw new TrackBuildError('non_finite_bank', `Bank is not finite at ${distances[index]}m.`);
    const bankedUp = normalize(rotateAroundAxis(rmf.up[index], tangent, bank));
    const bankedRight = normalize(cross(bankedUp, tangent));
    table.u[index] = sample.u;
    table.bank[index] = bank;
    table.sectionIndex[index] = sectionAt(sections, distances[index], trackLength, table.closed);
    writeVec(table.position, index, sample.position);
    writeVec(table.derivative, index, sample.derivative);
    writeVec(table.secondDerivative, index, sample.secondDerivative);
    writeVec(table.tangent, index, tangent);
    writeVec(table.curvature, index, curvature);
    writeVec(table.rmfUp, index, rmf.up[index]);
    writeVec(table.rmfRight, index, rmf.right[index]);
    writeVec(table.up, index, bankedUp);
    writeVec(table.right, index, bankedRight);
  }
  return table;
}

function normalizeDistance(distanceValue: number, lengthValue: number, closed: boolean): number {
  if (!Number.isFinite(distanceValue)) return 0;
  if (closed) return ((distanceValue % lengthValue) + lengthValue) % lengthValue;
  return clamp(distanceValue, 0, lengthValue);
}

export class TableTrackSampler implements TrackSampler {
  readonly valid = true;
  readonly issues: readonly TrackValidationIssue[] = [];
  readonly length: number;
  readonly closed: boolean;

  constructor(readonly table: ArcLengthTable) {
    this.length = table.length;
    this.closed = table.closed;
  }

  sampleByDistance(distanceValue: number): TrackSample {
    const target = normalizeDistance(distanceValue, this.length, this.closed);
    const distances = this.table.distance;
    let low = 0;
    let high = distances.length - 1;
    while (high - low > 1) {
      const middle = (low + high) >> 1;
      if (distances[middle] <= target) low = middle;
      else high = middle;
    }
    const span = distances[high] - distances[low];
    const t = span > EPSILON ? (target - distances[low]) / span : 0;
    const tangent = normalize(
      lerp(arrayVec(this.table.tangent, low), arrayVec(this.table.tangent, high), t),
    );
    const upSeed = normalize(lerp(arrayVec(this.table.up, low), arrayVec(this.table.up, high), t));
    const right = normalize(cross(upSeed, tangent));
    const up = normalize(cross(tangent, right));
    const rmfUpSeed = normalize(
      lerp(arrayVec(this.table.rmfUp, low), arrayVec(this.table.rmfUp, high), t),
    );
    const rmfRight = normalize(cross(rmfUpSeed, tangent));
    const rmfUp = normalize(cross(tangent, rmfRight));
    const sectionIndex = sectionAt(this.table.sections, target, this.length, this.closed);
    return {
      distance: target,
      u: this.table.u[low] * (1 - t) + this.table.u[high] * t,
      position: lerp(arrayVec(this.table.position, low), arrayVec(this.table.position, high), t),
      derivative: lerp(
        arrayVec(this.table.derivative, low),
        arrayVec(this.table.derivative, high),
        t,
      ),
      secondDerivative: lerp(
        arrayVec(this.table.secondDerivative, low),
        arrayVec(this.table.secondDerivative, high),
        t,
      ),
      tangent,
      curvature: lerp(arrayVec(this.table.curvature, low), arrayVec(this.table.curvature, high), t),
      rmfUp,
      rmfRight,
      bank: this.table.bank[low] * (1 - t) + this.table.bank[high] * t,
      up,
      right,
      section: sectionIndex >= 0 ? this.table.sections[sectionIndex] : null,
    };
  }
}

export class TrackCompiler {
  private readonly spanCache = new Map<number, SpanCacheEntry>();
  private mutableCounters = {
    builds: 0,
    spanEvaluations: 0,
    spanCacheHits: 0,
    invalidatedSpans: 0,
  };

  get counters(): TrackCompilerCounters {
    return { ...this.mutableCounters };
  }

  clear(): void {
    this.mutableCounters.invalidatedSpans += this.spanCache.size;
    this.spanCache.clear();
  }

  compile(
    definition: TrackDefinition,
    options?: AdaptiveTableOptions,
    changedControlPointIndices: readonly number[] = [],
  ): TableTrackSampler {
    const curve = new CubicBSpline(definition);
    if (changedControlPointIndices.length) {
      const changed = new Set(changedControlPointIndices);
      curve.spans.forEach((span) => {
        if (
          span.controlPointIndices.some((index) => changed.has(index)) &&
          this.spanCache.delete(span.index)
        ) {
          this.mutableCounters.invalidatedSpans += 1;
        }
      });
    }
    const resolvedOptions = mergeOptions(options);
    const samples: RawSample[] = [];
    curve.spans.forEach((span) => {
      const localPoints = span.controlPointIndices.map((index) => definition.controlPoints[index]);
      const signature = `${span.uStart}|${span.uEnd}|${resolvedOptions.maxSpacing}|${resolvedOptions.maxChordError}|${resolvedOptions.maxTangentAngle}|${localPoints.map((point) => `${point.x},${point.y},${point.z}`).join(';')}`;
      const cached = this.spanCache.get(span.index);
      let spanSamples: readonly RawSample[];
      if (cached?.signature === signature) {
        spanSamples = cached.samples;
        this.mutableCounters.spanCacheHits += 1;
      } else {
        spanSamples = adaptiveSpan(curve, span, resolvedOptions);
        this.spanCache.set(span.index, { signature, samples: spanSamples });
        this.mutableCounters.spanEvaluations += 1;
      }
      samples.push(...(samples.length ? spanSamples.slice(1) : spanSamples));
    });
    this.mutableCounters.builds += 1;
    return new TableTrackSampler(allocateTable(samples, definition));
  }
}

export function buildTrackSampler(
  definition: TrackDefinition,
  options?: AdaptiveTableOptions,
): TableTrackSampler {
  return new TrackCompiler().compile(definition, options);
}
